import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Paper, 
  FormGroup, 
  FormControlLabel, 
  Checkbox, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Grid, 
  MenuItem, 
  Alert 
} from '@mui/material';
import LanguageSwitcher from './LanguageSwitcher';
import { getContent } from '../utils/contentLoader';
import { getActivePractices } from '../services/patientService';
import { saveSelfPayPolicy, sendSelfPayPolicyEmail } from '../services/selfPayPolicyService';
import useDobConfirm from '../hooks/useDobConfirm';
import DobConfirmDialog from './DobConfirmDialog';

export default function SelfPayFinancialPolicy() {
  const { t, i18n } = useTranslation('selfPayPolicy');
  
  // Load language-aware content
  const selfPayContent = useMemo(() => getContent('selfPayFinancialPolicy', i18n.language), [i18n.language]);
  
  // Policy agreements state
  const [agreements, setAgreements] = useState({
    generalInformation: false,
    additionalCosts: false,
    paymentCancellation: false,
    medicalCancellation: false,
    incompletePerformance: false,
    reoperationRevisions: false,
  });

  // Patient identification fields
  const [practice, setPractice] = useState('');
  const [practiceCode, setPracticeCode] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientId, setPatientId] = useState(''); // PID - hidden field, URL only
  const [dob, setDobRaw] = useState('');
  const [gender, setGender] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState('');

  // Practices loading
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(true);
  const [practicesError, setPracticesError] = useState(null);

  // DOB confirmation
  const { dobInputRef, showDobConfirmDialog, handleDobBlur, handleDobChange: setDob, confirmDob, fixDob } = useDobConfirm(dob, setDobRaw);

  // Modal states for each policy section
  const [openModals, setOpenModals] = useState({
    generalInformation: false,
    additionalCosts: false,
    paymentCancellation: false,
    medicalCancellation: false,
    incompletePerformance: false,
    reoperationRevisions: false,
  });

  // Initials for each section
  const [initials, setInitials] = useState({
    generalInformation: '',
    additionalCosts: '',
    paymentCancellation: '',
    medicalCancellation: '',
    incompletePerformance: '',
    reoperationRevisions: '',
  });

  // Scroll tracking for each section
  const [scrolled, setScrolled] = useState({
    generalInformation: false,
    additionalCosts: false,
    paymentCancellation: false,
    medicalCancellation: false,
    incompletePerformance: false,
    reoperationRevisions: false,
  });

  // Refs for scroll detection
  const modalRefs = {
    generalInformation: useRef(null),
    additionalCosts: useRef(null),
    paymentCancellation: useRef(null),
    medicalCancellation: useRef(null),
    incompletePerformance: useRef(null),
    reoperationRevisions: useRef(null),
  };

  // Signature
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');

  // Submission state
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [returnTo] = useState(() => new URLSearchParams(window.location.search).get('returnTo') || '');

  // Load practices
  useEffect(() => {
    const loadPractices = async () => {
      try {
        const data = await getActivePractices();
        setPractices(data || []);
        setPracticesError(null);
      } catch (err) {
        console.error('Error loading practices:', err);
        setPracticesError('Failed to load practices');
        setPractices([{ practice_name: 'STARS Plastic Surgery', practice_code: 'STARS' }]);
      } finally {
        setPracticesLoading(false);
      }
    };
    loadPractices();

    // Pre-populate from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('practice')) setPractice(urlParams.get('practice'));
    if (urlParams.get('name')) setPatientName(urlParams.get('name'));
    if (urlParams.get('pid')) setPatientId(urlParams.get('pid')); // Patient ID from URL
    if (urlParams.get('dob')) setDob(urlParams.get('dob'));
    if (urlParams.get('gender')) setGender(urlParams.get('gender'));
    if (urlParams.get('phone')) setCellPhone(urlParams.get('phone'));
    if (urlParams.get('email')) setEmail(urlParams.get('email'));
  }, []);

  // Set default date
  useEffect(() => {
    if (!date) {
      const today = new Date();
      const iso = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
        .toISOString()
        .slice(0, 10);
      setDate(iso);
    }
  }, [date]);

  // Derive practiceCode from practice
  useEffect(() => {
    if (!practice) {
      setPracticeCode('');
      return;
    }
    const match = (practices || []).find((p) => p.practice_name === practice);
    setPracticeCode(match?.practice_code || '');
  }, [practice, practices]);

  // Phone formatting
  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setCellPhone(value);
  };

  const isValidPhone = cellPhone && cellPhone.length === 10;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(email);

  // Validation
  const idComplete = Boolean(
    practice && patientName && dob && gender && isValidPhone && isValidEmail
  );
  const allReviewed = Object.values(agreements).every(Boolean);
  const hasSignature = Boolean(signatureDataUrl);
  const canSubmit = idComplete && allReviewed && hasSignature;

  // Modal handlers
  const openModal = (section) => {
    setOpenModals(prev => ({ ...prev, [section]: true }));
    // Check if content needs scrolling after modal opens
    setTimeout(() => {
      const elem = modalRefs[section]?.current;
      if (elem) {
        const needsScroll = elem.scrollHeight > elem.clientHeight;
        if (!needsScroll) {
          // Content fits without scrolling, auto-enable
          setScrolled(prev => ({ ...prev, [section]: true }));
        }
      }
    }, 100);
  };

  const closeModal = (section, keepInitials = false) => {
    setOpenModals(prev => ({ ...prev, [section]: false }));
    // Only clear initials if user is canceling (not agreeing)
    if (!keepInitials) {
      setInitials(prev => ({ ...prev, [section]: '' }));
    }
    setScrolled(prev => ({ ...prev, [section]: false }));
  };

  const handleAgree = (section) => {
    if (!initials[section] || initials[section].trim().length < 2) {
      alert('Please enter your initials (at least 2 characters)');
      return;
    }
    if (!scrolled[section]) {
      alert('Please scroll through the entire policy before agreeing');
      return;
    }
    setAgreements(prev => ({ ...prev, [section]: true }));
    closeModal(section, true); // Keep the initials when agreeing
  };

  const handleScroll = (section) => (e) => {
    const elem = e.target;
    // More forgiving bottom detection (within 20px of bottom)
    const isAtBottom = elem.scrollHeight - elem.scrollTop - elem.clientHeight < 20;
    if (isAtBottom && !scrolled[section]) {
      setScrolled(prev => ({ ...prev, [section]: true }));
    }
  };

  // Signature canvas handlers
  const handleMouseDown = (e) => {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height)
    );
    setIsDrawing(true);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.lineTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height)
    );
    ctx.stroke();
  };

  const handleMouseUp = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        setSignatureDataUrl(canvas.toDataURL());
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureDataUrl('');
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError('Please complete all required fields, review all policies, and sign the form.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        practiceName: practice,
        practiceCode: practiceCode || null,
        patientName,
        patientId: patientId || null, // PID
        dob,
        gender,
        cellPhone: formatPhone(cellPhone),
        email,
        agreements,
        initials,
        signatureDataUrl,
        dateSigned: date,
        formType: 'selfPayFinancialPolicy',
        timestamp: new Date().toISOString()
      };

      console.log('Submitting Self-pay Financial Policy:', payload);

      // Save policy to database and upload to S3
      const result = await saveSelfPayPolicy(payload);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save policy agreement');
      }

      console.log('✅ Policy agreement saved successfully with ID:', result.id);

      // Send confirmation email with PDF attachment
      try {
        const emailResult = await sendSelfPayPolicyEmail(payload);
        if (emailResult.success) {
          console.log('✅ Confirmation email sent successfully');
        } else {
          console.warn('⚠️ Email sending failed:', emailResult.error);
          // Don't fail the whole submission if email fails
        }
      } catch (emailErr) {
        console.error('Email sending error:', emailErr);
        // Continue even if email fails
      }

      setSubmitSuccess(true);
      // If launched from estimate share page, redirect back after a brief delay
      if (returnTo) {
        setTimeout(() => { window.location.href = returnTo; }, 2000);
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to submit policy agreement. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render modal for a policy section
  const renderPolicyModal = (section, sectionData) => (
    <Dialog 
      open={openModals[section]} 
      onClose={() => closeModal(section)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{sectionData.title}</DialogTitle>
      <DialogContent>
        <Box
          ref={modalRefs[section]}
          onScroll={handleScroll(section)}
          sx={{ 
            maxHeight: '300px', 
            overflow: 'auto', 
            mb: 2,
            p: 2,
            bgcolor: '#f9f9f9',
            borderRadius: 1,
            border: '1px solid #ddd'
          }}
        >
          {sectionData.content.map((paragraph, idx) => (
            <Typography 
              key={idx} 
              variant="body2" 
              sx={{ mb: 1.5, whiteSpace: 'pre-wrap', textAlign: 'justify' }}
            >
              {paragraph.replace(/\[Medical practice\]/g, practice || '[Practice Name]')}
            </Typography>
          ))}
        </Box>
        {scrolled[section] && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('scrollMessages.scrolledThrough')}
          </Alert>
        )}
        <TextField
          fullWidth
          label={t('labels.yourInitials')}
          value={initials[section]}
          onChange={(e) => setInitials(prev => ({ ...prev, [section]: e.target.value }))}
          placeholder={t('placeholders.initials')}
          helperText={
            scrolled[section] 
              ? t('scrollMessages.enterInitialsToAgree') 
              : t('scrollMessages.scrollAndEnterInitials')
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => closeModal(section)}>{t('buttons.cancel')}</Button>
        <Button 
          onClick={() => handleAgree(section)} 
          variant="contained" 
          color="primary"
          disabled={!scrolled[section] || !initials[section] || initials[section].trim().length < 2}
        >
          {t('buttons.iAgree')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {submitSuccess ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('success.title')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('success.thankYou', { practice })}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('success.emailSent', { email })}
            </Typography>
            {returnTo ? (
              <Typography variant="body2">
                Redirecting you back to your estimate…
              </Typography>
            ) : (
              <Typography variant="body2">
                {t('success.questions')}
              </Typography>
            )}
          </Alert>
          {!returnTo && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button variant="contained" color="primary" href="/">
                {t('buttons.returnToHome')}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <LanguageSwitcher />
          </Box>
          <Typography variant="h4" align="center" gutterBottom>
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom sx={{ mb: 3 }}>
            {t('subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Patient Identification */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('sections.patientIdentification')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t('labels.practice')}
                  value={practice}
                  onChange={(e) => setPractice(e.target.value)}
                  disabled={practicesLoading}
                  error={!!practicesError}
                  helperText={practicesError || ''}
                >
                  {(practices || []).map((p) => (
                    <MenuItem key={p.practice_name} value={p.practice_name}>
                      {p.practice_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  required
                  label={t('labels.fullName')} 
                  value={patientName} 
                  onChange={(e) => setPatientName(e.target.value)} 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  required
                  type="date" 
                  label={t('labels.dateOfBirth')} 
                  InputLabelProps={{ shrink: true }} 
                  inputRef={dobInputRef}
                  value={dob} 
                  onChange={(e) => setDob(e.target.value)} 
                  onBlur={handleDobBlur}
                />
                <DobConfirmDialog open={showDobConfirmDialog} dob={dob} onFix={fixDob} onConfirm={confirmDob} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  select 
                  fullWidth 
                  required
                  label={t('labels.gender')} 
                  value={gender} 
                  onChange={(e) => setGender(e.target.value)}
                >
                  <MenuItem value="Male">{t('genderOptions.male')}</MenuItem>
                  <MenuItem value="Female">{t('genderOptions.female')}</MenuItem>
                  <MenuItem value="Other">{t('genderOptions.other')}</MenuItem>
                  <MenuItem value="Prefer not to say">{t('genderOptions.preferNotToSay')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t('labels.cellPhone')}
                  value={formatPhone(cellPhone)}
                  onChange={handlePhoneChange}
                  placeholder={t('placeholders.phone')}
                  error={cellPhone && cellPhone.length !== 10}
                  helperText={cellPhone && cellPhone.length !== 10 ? t('errors.validPhone') : ''}
                  inputMode="tel"
                  inputProps={{ maxLength: 12 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label={t('labels.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('placeholders.email')}
                  error={email && !isValidEmail}
                  helperText={email && !isValidEmail ? t('errors.validEmail') : ''}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Policy Sections with Checkboxes */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('sections.financialPolicySections')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('instructions.reviewSections')}
            </Typography>
            <FormGroup>
              {Object.keys(selfPayContent).map((sectionKey) => (
                <Box key={sectionKey} sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreements[sectionKey]}
                        disabled={true}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{selfPayContent[sectionKey].title}</Typography>
                        {initials[sectionKey] && (
                          <Typography variant="caption" sx={{ color: 'success.main' }}>
                            ({t('labels.initials')}: {initials[sectionKey]})
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => openModal(sectionKey)}
                    sx={{ ml: 4, mb: 1 }}
                  >
                    {agreements[sectionKey] ? t('buttons.reviewAgain') : t('buttons.reviewAndAgree')}
                  </Button>
                </Box>
              ))}
            </FormGroup>
          </Paper>

          {/* Signature Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('sections.signature')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('instructions.signatureDisclaimer')}
            </Typography>
            <Box sx={{ border: '2px solid #ccc', borderRadius: 1, mb: 2, position: 'relative' }}>
              {!signatureDataUrl && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#999',
                    pointerEvents: 'none',
                  }}
                >
                  {t('instructions.signHere')}
                </Typography>
              )}
              <canvas
                ref={canvasRef}
                width={800}
                height={200}
                style={{ width: '100%', height: '200px', cursor: 'crosshair', display: 'block' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={(e) => {
                  e.preventDefault();
                  const touch = e.touches[0];
                  const canvas = e.currentTarget;
                  const rect = canvas.getBoundingClientRect();
                  const ctx = canvas.getContext('2d');
                  ctx.strokeStyle = '#000';
                  ctx.lineWidth = 2;
                  ctx.lineCap = 'round';
                  ctx.beginPath();
                  ctx.moveTo(
                    (touch.clientX - rect.left) * (canvas.width / rect.width),
                    (touch.clientY - rect.top) * (canvas.height / rect.height)
                  );
                  setIsDrawing(true);
                }}
                onTouchMove={(e) => {
                  if (!isDrawing) return;
                  e.preventDefault();
                  const touch = e.touches[0];
                  const canvas = e.currentTarget;
                  const rect = canvas.getBoundingClientRect();
                  const ctx = canvas.getContext('2d');
                  ctx.lineTo(
                    (touch.clientX - rect.left) * (canvas.width / rect.width),
                    (touch.clientY - rect.top) * (canvas.height / rect.height)
                  );
                  ctx.stroke();
                }}
                onTouchEnd={handleMouseUp}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button variant="outlined" onClick={clearSignature}>
                {t('buttons.clearSignature')}
              </Button>
              <TextField
                type="date"
                label={t('labels.date')}
                InputLabelProps={{ shrink: true }}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                sx={{ minWidth: 200 }}
              />
            </Box>
          </Paper>

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              sx={{ minWidth: 200 }}
            >
              {isSubmitting ? t('buttons.submitting') : t('buttons.submitAgreement')}
            </Button>
          </Box>

          {!canSubmit && (
            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
              {t('errors.incompleteForm')}
            </Typography>
          )}
        </>
      )}

      {/* Render modals for each section */}
      {Object.keys(selfPayContent).map((sectionKey) => renderPolicyModal(sectionKey, selfPayContent[sectionKey]))}
    </Box>
  );
}
